import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as XLSX from 'xlsx';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { Subject, debounceTime, firstValueFrom } from 'rxjs';
import { MainLayoutComponent } from '../../_layouts/main-layout/main-layout.component';
import { ConfirmDialogComponent } from '../../../Shared/_components/confirm-dialog/confirm-dialog.component';
import { AlertService } from '../../_services/alert.service';
import { UserService } from '../../_services/users.service';
import { InventoryService, InventoryQuery, InventoryApiRow } from '../../_services/inventory.service';
import { InventoryItem } from '../../_interfaces/inventory-item';
import { StockHistoryComponent } from './stock-history/stock-history.component';
import { InventoryListState, PLACEHOLDER_QTY } from './inventory-edit/inventory-edit.component';
import { ImportPreviewComponent, ImportPreviewData, ImportPreviewResult } from './import-preview/import-preview.component';
import { planImport } from './inventory-import';
import { BarcodeLabelsComponent } from './barcode-labels/barcode-labels.component';
import { AppPaginatorIntl } from '../../../Shared/_services/app-paginator-intl';
import { PaginatorPagesDirective } from '../../../Shared/_directives/paginator-pages.directive';

type StockStatus = 'in' | 'low' | 'out';

// A row as shown on screen: the saved stock is remembered so a stock count
// can tell which rows have been changed.
interface InventoryRow extends InventoryItem {
  savedKettering: number;
  savedMarkstey: number;
  saveError?: string;
}

// Matches the low-stock threshold used elsewhere in the backend
// (orders.service.ts: `stockQty - orderItem.quantity < 5`) - there's no
// per-product reorder point in the schema.
const LOW_STOCK_THRESHOLD = 5;

@Component({
    selector: 'app-inventory',
    imports: [PaginatorPagesDirective,
        CommonModule,
        FormsModule,
        MainLayoutComponent,
        MatIconModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatPaginatorModule,
    ],
    // The pages here are pages of products, not of rows.
    providers: [
        {
            provide: MatPaginatorIntl,
            useFactory: () => {
                const intl = new MatPaginatorIntl();
                intl.itemsPerPageLabel = 'Products per page:';
                return intl;
            },
        },
    ],
    templateUrl: './inventory.component.html',
    styleUrl: './inventory.component.css'
})
export class InventoryComponent implements OnInit, OnDestroy {
  private readonly dialog = inject(MatDialog);
  private readonly alertService = inject(AlertService);
  private readonly inventoryService = inject(InventoryService);
  private readonly userService = inject(UserService);
  private readonly paginatorIntl = inject(MatPaginatorIntl);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly lowStockThreshold = LOW_STOCK_THRESHOLD;

  rows: InventoryRow[] = [];
  // The rows again, with the variants of one product together.
  groups: { productId: number; name: string; category: string | null; image: string | null; rows: InventoryRow[] }[] = [];
  count = 0; // products when grouped, otherwise items
  variantCount = 0; // always the items
  // Cost prices: super admins and the purchasing team only (the server decides).
  canSeeCost = false;
  // The purchasing team can look but not change anything.
  readonly canEdit = (() => {
    const roles = String(this.userService.isLoggedIn().userrole ?? '').split(',').map((r: string) => r.trim());
    return ['S-ADMIN', 'ADMIN', 'SALEADMIN', 'MARKETING'].some((r) => roles.includes(r));
  })();
  pageNumber = 0;
  itemsPerPage = 20;
  loading = true;
  loadError = false;
  exporting = false;
  importing = false;

  // From the whole filtered result set, not just the current page.
  unitsOnHand = 0;
  stockValue = 0;
  lowStockCount = 0;
  outOfStockCount = 0;
  statusCounts = { all: 0, in: 0, low: 0, out: 0 };

  categoryOptions: string[] = ['ALL'];
  readonly storeOptions = [
    { value: 'ALL', label: 'Both yards' },
    // Kettering is hidden from the filter for now (its stock still counts in
    // "Both yards" and the export); add { value: 'Kettering', ... } back to show it.
    { value: 'Markstey', label: 'Markstey' },
  ];
  readonly statusOptions = [
    { value: 'ALL', label: 'All', key: 'all' },
    { value: 'IN STOCK', label: 'In stock', key: 'in' },
    { value: 'LOW STOCK', label: 'Low stock', key: 'low' },
    { value: 'OUT OF STOCK', label: 'Out of stock', key: 'out' },
  ] as const;
  readonly sortOptions = [
    { value: 'name', label: 'Name A-Z' },
    { value: 'stockAsc', label: 'Lowest stock first' },
    { value: 'stockDesc', label: 'Highest stock first' },
    { value: 'priceDesc', label: 'Highest price first' },
    { value: 'priceAsc', label: 'Lowest price first' },
  ];

  search = '';
  category = 'ALL';
  status = 'ALL';
  store = 'ALL';
  onSale = false;
  sort = 'name';
  // Off: the plain list, one row per variant. On: variants of one product together
  // under the product's name (and the pages are pages of products). Remembered.
  groupByProduct = (() => {
    try { return localStorage.getItem('boInventoryGrouped') === 'true'; } catch { return false; }
  })();

  // Stock count mode: the stock cells turn into inputs, and everything
  // changed is saved together.
  countMode = false;
  savingAll = false;
  saveProgress = 0;

  // Ticked items to export, by variant id. Kept across pages, searches and
  // filters, so a selection can be built up from several searches.
  readonly selected = new Map<number, InventoryItem>();

  private readonly search$ = new Subject<void>();
  private loadSeq = 0;

  constructor() {
    this.search$.pipe(debounceTime(350), takeUntilDestroyed()).subscribe(() => this.applyFilters());
  }

  ngOnInit(): void {
    // ?q= opens the list already searched - e.g. a SKU clicked on a shipment.
    // The rest comes back from an item's edit page, so the list is as you left it.
    const qp = this.route.snapshot.queryParamMap;
    const q = qp.get('q');
    if (q) this.search = q;
    if (qp.get('cat')) this.category = qp.get('cat')!;
    if (qp.get('status')) this.status = qp.get('status')!;
    if (qp.get('store')) this.store = qp.get('store')!;
    if (qp.get('sale') === '1') this.onSale = true;
    if (qp.get('sort')) this.sort = qp.get('sort')!;
    if (qp.has('group')) this.groupByProduct = qp.get('group') === '1';
    if (qp.get('page')) this.pageNumber = Math.max(0, Number(qp.get('page')) || 0);
    if (qp.get('size')) this.itemsPerPage = Math.max(1, Number(qp.get('size')) || 20);
    this.syncPaginatorLabel();
    this.inventoryService.getCategories().subscribe({
      next: (categories) => (this.categoryOptions = ['ALL', ...categories]),
      // Non-fatal: the category filter just stays at "ALL" only.
      error: () => {},
    });
    this.loadInventory();
  }

  get filtersActive(): boolean {
    return !!(this.search || this.category !== 'ALL' || this.status !== 'ALL' || this.store !== 'ALL' || this.onSale);
  }

  private query(pageIndex: number, pageSize: number): InventoryQuery {
    return {
      pageIndex,
      pageSize,
      search: this.search.trim() || undefined,
      category: this.category,
      store: this.store,
      status: this.status,
      onSale: this.onSale || undefined,
      sort: this.sort,
      groupByProduct: this.groupByProduct,
    };
  }

  // ---- Loading ----

  loadInventory(quiet = false): void {
    const seq = ++this.loadSeq;
    if (!quiet) this.loading = true;
    this.loadError = false;
    this.inventoryService.getInventory(this.query(this.pageNumber, this.itemsPerPage)).subscribe({
      next: (response) => {
        if (seq !== this.loadSeq) return;
        this.rows = response.items.map((r) => ({
          ...this.toItem(r),
          savedKettering: r.kStockQty,
          savedMarkstey: r.mStockQty,
        }));
        this.count = response.count;
        this.variantCount = response.variantCount;
        this.canSeeCost = response.canSeeCost;
        this.groups = this.groupByProduct ? this.groupRows(this.rows) : this.rows.map((r) => this.singleGroup(r));
        // Ticked items on this page: keep their latest figures for the export.
        for (const r of this.rows) if (this.selected.has(r.productVariantId)) this.selected.set(r.productVariantId, r);
        this.unitsOnHand = response.unitsOnHand;
        this.stockValue = response.stockValue;
        this.lowStockCount = response.lowStockCount;
        this.outOfStockCount = response.outOfStockCount;
        this.statusCounts = response.statusCounts;
        this.loading = false;
      },
      error: () => {
        if (seq !== this.loadSeq) return;
        this.loading = false;
        this.loadError = true;
        this.alertService.alert('Could not load inventory', 'error');
      },
    });
  }

  // The server sends each product's variants one after another; gather them.
  private groupRows(rows: InventoryRow[]): InventoryComponent['groups'] {
    const groups: InventoryComponent['groups'] = [];
    for (const row of rows) {
      let g = groups.length ? groups[groups.length - 1] : undefined;
      if (!g || g.productId !== row.productId) {
        g = { productId: row.productId, name: row.productName, category: row.category, image: row.image, rows: [] };
        groups.push(g);
      }
      if (!g.image && row.image) g.image = row.image;
      g.rows.push(row);
    }
    return groups;
  }

  // The plain list: every row stands alone.
  private singleGroup(row: InventoryRow): InventoryComponent['groups'][number] {
    return { productId: row.productId, name: row.productName, category: row.category, image: row.image, rows: [row] };
  }

  toggleGrouping(): void {
    this.ifNothingUnsaved(() => {
      this.groupByProduct = !this.groupByProduct;
      try { localStorage.setItem('boInventoryGrouped', String(this.groupByProduct)); } catch { /* private window: just not remembered */ }
      this.syncPaginatorLabel();
      this.pageNumber = 0;
      this.loadInventory();
    });
  }

  // The paginator labels are shared by every page - put the default back.
  ngOnDestroy(): void {
    this.paginatorIntl.itemsPerPageLabel = AppPaginatorIntl.DEFAULT_ITEMS_LABEL;
    this.paginatorIntl.changes.next();
  }

  private syncPaginatorLabel(): void {
    this.paginatorIntl.itemsPerPageLabel = this.groupByProduct ? 'Products per page' : 'Items per page';
    this.paginatorIntl.changes.next();
  }

  copyBarcode(row: InventoryRow): void {
    this.copy(row.barcode, 'Barcode');
  }

  copySku(row: InventoryRow): void {
    this.copy(row.sku, 'SKU');
  }

  private copy(text: string | null | undefined, what: string): void {
    if (!text) return;
    navigator.clipboard?.writeText(text).then(() => this.alertService.alert(`${what} copied`, 'success'));
  }

  // ---- Filters (each asks first if stock counts would be lost) ----

  onSearchInput(): void {
    this.search$.next();
  }

  applyFilters(): void {
    this.ifNothingUnsaved(() => {
      this.pageNumber = 0;
      this.loadInventory();
    });
  }

  setStore(value: string): void {
    if (value === this.store) return;
    this.ifNothingUnsaved(() => {
      this.store = value;
      this.pageNumber = 0;
      this.loadInventory();
    });
  }

  setStatus(value: string): void {
    // Clicking the chosen chip again goes back to "All".
    const next = this.status === value ? 'ALL' : value;
    this.ifNothingUnsaved(() => {
      this.status = next;
      this.pageNumber = 0;
      this.loadInventory();
    });
  }

  toggleOnSale(): void {
    this.ifNothingUnsaved(() => {
      this.onSale = !this.onSale;
      this.pageNumber = 0;
      this.loadInventory();
    });
  }

  onResetFilters(): void {
    this.ifNothingUnsaved(() => {
      this.search = '';
      this.category = 'ALL';
      this.status = 'ALL';
      this.store = 'ALL';
      this.onSale = false;
      this.pageNumber = 0;
      this.loadInventory();
    });
  }

  updateList(event: PageEvent): void {
    this.ifNothingUnsaved(() => {
      if (this.itemsPerPage !== event.pageSize) {
        this.itemsPerPage = event.pageSize || 20;
        this.pageNumber = 0;
      } else {
        this.pageNumber = event.pageIndex;
      }
      this.loadInventory();
    });
  }

  // ---- What a row shows ----

  qtyOf(row: InventoryRow, field: string): number {
    return field === 'kettering' ? row.kettering : row.markstey;
  }

  savedQtyOf(row: InventoryRow, field: string): number {
    return field === 'kettering' ? row.savedKettering : row.savedMarkstey;
  }

  setQty(row: InventoryRow, field: string, value: any): void {
    if (field === 'kettering') row.kettering = value;
    else row.markstey = value;
  }

  isUnlimited(qty: number): boolean {
    return qty >= PLACEHOLDER_QTY;
  }

  // Judged on the store picked, or on both together.
  private qtyFor(row: InventoryRow): number {
    if (this.store === 'Kettering') return row.savedKettering;
    if (this.store === 'Markstey') return row.savedMarkstey;
    return row.savedKettering + row.savedMarkstey;
  }

  rowStatus(row: InventoryRow): StockStatus {
    const qty = this.qtyFor(row);
    if (qty <= 0) return 'out';
    if (qty < LOW_STOCK_THRESHOLD) return 'low';
    return 'in';
  }

  rowTotal(row: InventoryRow): number {
    return (Number(row.kettering) || 0) + (Number(row.markstey) || 0);
  }

  // How full the little bar under the total is: full at 4x the low-stock
  // line, empty at none - a quick feel for the level, not an exact scale.
  stockLevel(row: InventoryRow): number {
    const total = this.rowTotal(row);
    if (total <= 0) return 0;
    return Math.max(8, Math.min(100, Math.round((total / (this.lowStockThreshold * 4)) * 100)));
  }

  statusLabel(status: StockStatus): string {
    return status === 'in' ? 'In stock' : status === 'low' ? 'Low stock' : 'Out of stock';
  }

  statusClass(status: StockStatus): string {
    return status === 'in' ? 'bg-emerald-50 text-emerald-700' : status === 'low' ? 'bg-amber-100 text-amber-800' : 'bg-rose-50 text-rose-700';
  }

  statusDot(status: StockStatus): string {
    return status === 'in' ? 'bg-emerald-500' : status === 'low' ? 'bg-amber-500' : 'bg-rose-500';
  }

  // What customers pay: the sale price when there is one.
  sellingPrice(row: InventoryRow): number {
    return Number(row.discountedPrice) > 0 ? Number(row.discountedPrice) : Number(row.regularPrice);
  }

  margin(row: InventoryRow): number | null {
    const price = this.sellingPrice(row);
    return row.costPrice !== null && row.costPrice !== undefined && price > 0
      ? Math.round(((price - Number(row.costPrice)) / price) * 100)
      : null;
  }

  // ---- Editing one item ----

  // The list as it stands, for the edit page to step through and come back to.
  private get listState(): InventoryListState {
    const s: InventoryListState = {};
    if (this.search.trim()) s.q = this.search.trim();
    if (this.category !== 'ALL') s.cat = this.category;
    if (this.status !== 'ALL') s.status = this.status;
    if (this.store !== 'ALL') s.store = this.store;
    if (this.onSale) s.sale = '1';
    if (this.sort !== 'name') s.sort = this.sort;
    s.group = this.groupByProduct ? '1' : '0';
    if (this.pageNumber) s.page = String(this.pageNumber);
    if (this.itemsPerPage !== 20) s.size = String(this.itemsPerPage);
    return s;
  }

  openEdit(row: InventoryRow): void {
    this.router.navigate(['/inventory/edit', row.productVariantId], { queryParams: this.listState });
  }

  openHistory(row: InventoryRow): void {
    this.dialog.open(StockHistoryComponent, {
      width: '1000px',
      maxWidth: '96vw',
      data: {
        productVariantId: row.productVariantId,
        productName: row.productName,
        sku: row.sku,
        label: row.label,
        barcode: row.barcode,
        image: row.image,
      },
    });
  }

  // ---- Stock count mode ----

  get changedRows(): InventoryRow[] {
    return this.rows.filter((r) => this.isChanged(r));
  }

  isChanged(row: InventoryRow): boolean {
    return Number(row.kettering) !== row.savedKettering || Number(row.markstey) !== row.savedMarkstey;
  }

  invalidStock(row: InventoryRow): boolean {
    const whole = (n: any) => Number.isInteger(Number(n)) && Number(n) >= 0 && n !== null && n !== '';
    return !whole(row.kettering) || !whole(row.markstey);
  }

  toggleCountMode(): void {
    if (!this.canEdit) return;
    if (this.countMode) {
      this.ifNothingUnsaved(() => (this.countMode = false));
    } else {
      this.countMode = true;
    }
  }

  discardChanges(): void {
    for (const row of this.rows) {
      row.kettering = row.savedKettering;
      row.markstey = row.savedMarkstey;
      row.saveError = undefined;
    }
  }

  async saveAll(): Promise<void> {
    const changed = this.changedRows;
    if (!changed.length || this.savingAll) return;
    if (changed.some((r) => this.invalidStock(r))) {
      this.alertService.alert('Stock must be a whole number, zero or more', 'error');
      return;
    }
    this.savingAll = true;
    this.saveProgress = 0;
    let saved = 0;
    for (const row of changed) {
      try {
        await new Promise<void>((resolve, reject) =>
          this.inventoryService
            .updateVariant(row.productVariantId, {
              kStockQty: Number(row.kettering),
              mStockQty: Number(row.markstey),
              reason: 'Stock count',
            })
            .subscribe({ next: () => resolve(), error: (e) => reject(e) }),
        );
        row.kettering = Number(row.kettering);
        row.markstey = Number(row.markstey);
        row.savedKettering = row.kettering;
        row.savedMarkstey = row.markstey;
        row.saveError = undefined;
        saved++;
      } catch (e: any) {
        row.saveError = e?.error?.message || 'Could not save';
      }
      this.saveProgress++;
    }
    this.savingAll = false;
    const failed = changed.length - saved;
    this.alertService.alert(
      failed ? `${saved} saved, ${failed} could not be saved - they are marked in the list` : `${saved} stock count${saved === 1 ? '' : 's'} saved`,
      failed ? 'error' : 'success',
    );
    this.loadInventory(true);
  }

  // Runs the action, first asking whether to drop unsaved stock counts.
  private ifNothingUnsaved(action: () => void): void {
    const n = this.changedRows.length;
    if (!n || !this.countMode) {
      action();
      return;
    }
    this.dialog
      .open(ConfirmDialogComponent, {
        width: '460px',
        maxWidth: '96vw',
        data: {
          title: 'Discard stock counts?',
          message: `You have ${n} unsaved stock change${n === 1 ? '' : 's'}.`,
          detail: 'Save them first, or carry on and they are dropped.',
          confirmText: 'Discard and continue',
          cancelText: 'Go back',
          tone: 'warning',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.confirmation) {
          this.discardChanges();
          action();
        }
      });
  }

  // A row from the server in the shape the page (and the export) uses.
  private toItem(r: InventoryApiRow): InventoryItem {
    return {
      productId: r.productId,
      productVariantId: r.productVariantId,
      productName: r.productName,
      sku: r.sku,
      barcode: r.barcode,
      label: r.label,
      image: r.image,
      category: r.category,
      kettering: r.kStockQty,
      markstey: r.mStockQty,
      regularPrice: r.price,
      discountedPrice: r.discPrice,
      costPrice: r.costPrice ?? null,
      priceMS: r.priceMS,
      approxCoverage: r.approxCoverage,
    };
  }

  // ---- Selecting items to export ----

  isSelected(row: InventoryItem): boolean {
    return this.selected.has(row.productVariantId);
  }

  toggleRow(row: InventoryItem): void {
    if (this.selected.has(row.productVariantId)) this.selected.delete(row.productVariantId);
    else this.selected.set(row.productVariantId, row);
  }

  // all / some / none of these rows ticked - for a heading's tick box.
  tickState(rows: InventoryItem[]): 'all' | 'some' | 'none' {
    const n = rows.filter((r) => this.selected.has(r.productVariantId)).length;
    return n === 0 ? 'none' : n === rows.length ? 'all' : 'some';
  }

  // A product heading or the column heading: tick all of them, or untick
  // all of them when they're all ticked already.
  toggleRows(rows: InventoryItem[]): void {
    const tickAll = this.tickState(rows) !== 'all';
    for (const r of rows) {
      if (tickAll) this.selected.set(r.productVariantId, r);
      else this.selected.delete(r.productVariantId);
    }
  }

  clearSelection(): void {
    this.selected.clear();
  }

  // ---- Import (an edited export) ----

  // The file picker's change event: read the sheet, compare it with what's
  // in the system now, and show the changes for the person to confirm.
  onImportFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = ''; // so picking the same file again still triggers
    if (!file || this.importing) return;
    this.ifNothingUnsaved(() => this.readImport(file));
  }

  private async readImport(file: File): Promise<void> {
    this.importing = true;
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = sheet ? XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' }) : [];
      if (!rows.length) {
        this.alertService.alert('That file has no rows to import', 'error');
        return;
      }
      // Every item as it is now, whatever the page is filtered to.
      const all = await firstValueFrom(
        this.inventoryService.getInventory({ pageIndex: 0, pageSize: 100000, category: 'ALL', store: 'ALL', status: 'ALL', sort: 'name', groupByProduct: false }),
      );
      const plan = planImport(rows, new Map(all.items.map((r) => [r.productVariantId, r])), all.canSeeCost);
      const data: ImportPreviewData = { plan, fileName: file.name };
      this.dialog
        .open<ImportPreviewComponent, ImportPreviewData, ImportPreviewResult>(ImportPreviewComponent, { data, width: '860px', maxWidth: '96vw', maxHeight: '90vh' })
        .afterClosed()
        .subscribe((result) => {
          if (result?.saved) {
            this.alertService.alert(`${result.saved} item${result.saved === 1 ? '' : 's'} updated from ${file.name}`, result.failed ? 'error' : 'success');
            this.loadInventory(true);
          }
        });
    } catch {
      this.alertService.alert('Could not read that file - use an .xlsx downloaded with Export', 'error');
    } finally {
      this.importing = false;
    }
  }

  // ---- Export ----

  // Just the ticked items (the selection bar's button).
  // Barcode labels (Odoo's layout) for the ticked products, or one row.
  printLabels(items?: InventoryItem[]): void {
    const list = items ?? [...this.selected.values()];
    if (!list.length) return;
    this.dialog.open(BarcodeLabelsComponent, { width: '860px', maxWidth: '96vw', data: list });
  }

  exportSelected(): void {
    if (!this.selected.size) return;
    const items = [...this.selected.values()].sort((a, b) => a.productName.localeCompare(b.productName) || String(a.sku).localeCompare(String(b.sku)));
    this.writeWorkbook(items, this.canSeeCost, `inventory-selected-${items.length}-${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  // Every item matching the current filters, not just the page on screen
  // (the header's Export button - ticks don't change it).
  exportToExcel(): void {
    if (this.exporting) return;
    this.exporting = true;
    this.inventoryService.getInventory(this.query(0, Math.max(this.count, 1))).subscribe({
      next: (response) => {
        this.writeWorkbook(response.items.map((r) => this.toItem(r)), response.canSeeCost, `inventory-${new Date().toISOString().slice(0, 10)}.xlsx`);
        this.exporting = false;
      },
      error: () => {
        this.exporting = false;
        this.alertService.alert('Could not export inventory', 'error');
      },
    });
  }

  private writeWorkbook(items: InventoryItem[], canSeeCost: boolean, fileName: string): void {
    // Saved stock only: a stock count's typed-but-unsaved numbers stay out.
    const yard = (r: InventoryItem, saved: 'savedKettering' | 'savedMarkstey', live: number) =>
      ((r as Partial<InventoryRow>)[saved] ?? live) || 0;
    const sheetRows = items.map((r) => ({
      'ID': r.productVariantId,
      'Name': r.productName,
      'SKU': r.sku,
      'Regular Price': r.regularPrice !== null && r.regularPrice !== undefined ? Number(r.regularPrice) : '',
      'Discounted Price': r.discountedPrice ? Number(r.discountedPrice) : '',
      'Approx Coverage': r.approxCoverage ?? '',
      'Price /m²': r.priceMS ?? '',
      'Category': r.category ?? '',
      'Kettering': yard(r, 'savedKettering', r.kettering),
      'Markstey': yard(r, 'savedMarkstey', r.markstey),
      'Total Stock': yard(r, 'savedKettering', r.kettering) + yard(r, 'savedMarkstey', r.markstey),
      'Barcode': r.barcode ?? '',
      // Only present for people allowed to see it.
      ...(canSeeCost ? { 'Cost Price': r.costPrice !== null && r.costPrice !== undefined ? Number(r.costPrice) : '' } : {}),
    }));
    const worksheet = XLSX.utils.json_to_sheet(sheetRows);
    worksheet['!cols'] = [8, 60, 30, 14, 16, 16, 12, 24, 11, 11, 12, 18, 12].map((wch) => ({ wch }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Inventory');
    XLSX.writeFile(workbook, fileName);
  }
}
